import { createFileRoute, useSearch } from '@tanstack/react-router'
import React, { useState } from 'react'
import { Input } from '@/src/components/ui/input'
import { Textarea } from '@/src/components/ui/textarea'
import { Button } from '@/src/components/ui/button'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  SelectLabel,
  SelectGroup,
} from '@/src/components/ui/select'
import { Label } from '@/src/components/ui/label'
import { Field } from '@base-ui/react/field'
import { useTranslation } from '@/src/i18n/client'

export const Route = createFileRoute('/_static/contact')({
  validateSearch: (search: Record<string, unknown>): { topic?: string } => ({
    topic: search.topic as string | undefined,
  }),
  component: ContactPage,
})

function ContactForm() {
  const { t } = useTranslation('static')
  const { topic: initialTopic } = useSearch({ from: '/_static/contact' })
  const [form, setForm] = useState({
    name: '',
    email: '',
    topic: initialTopic || '',
    message: '',
  })
  const [success, setSuccess] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    setForm({ ...form, [e.target.name]: e.target.value })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSuccess(null)
    setError(null)
    setIsSubmitting(true)
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      })
      const data = await res.json()
      if (data.success) {
        setSuccess(t('contact.successMessage'))
        setForm({ name: '', email: '', topic: initialTopic || '', message: '' })
      } else {
        setError(
          data.error || t('contact.errorMessage'),
        )
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t('contact.errorMessage'),
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form
      className="space-y-6 bg-card rounded-xl shadow-lg p-6 border"
      onSubmit={handleSubmit}
    >
      <Field.Root name="name">
        <div>
          <Label htmlFor="name" className="block mb-1 font-medium">
            {t('contact.name')}
          </Label>
          <Input
            id="name"
            name="name"
            value={form.name}
            onChange={handleChange}
            required
            autoComplete="name"
            placeholder={t('contact.namePlaceholder')}
          />
        </div>
      </Field.Root>
      <Field.Root name="email">
        <div>
          <Label htmlFor="email" className="block mb-1 font-medium">
            {t('contact.email')}
          </Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={form.email}
            onChange={handleChange}
            required
            autoComplete="email"
            placeholder={t('contact.emailPlaceholder')}
          />
        </div>
      </Field.Root>
      <Field.Root name="topic">
        <div>
          <Label htmlFor="topic" className="block mb-1 font-medium">
            {t('contact.topic')}
          </Label>
          <Select
            value={form.topic}
            onValueChange={(value) => value && setForm({ ...form, topic: value })}
          >
            <SelectTrigger className="w-full" id="topic">
              <SelectValue placeholder={t('contact.topicPlaceholder')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Sales Inquiry">{t('contact.topicSalesInquiry')}</SelectItem>
              <SelectItem value="Support">{t('contact.topicSupport')}</SelectItem>
              <SelectItem value="Feedback">{t('contact.topicFeedback')}</SelectItem>
              <SelectItem value="Other">{t('contact.topicOther')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Field.Root>
      <Field.Root name="message">
        <div>
          <Label htmlFor="message" className="block mb-1 font-medium">
            {t('contact.message')}
          </Label>
          <Textarea
          id="message"
          name="message"
          value={form.message}
          onChange={handleChange}
          required
          rows={6}
          placeholder={t('contact.messagePlaceholder')}
        />
      </div>
      </Field.Root>
      <Button type="submit" disabled={isSubmitting} className="w-full">
        {isSubmitting ? t('contact.sending') : t('contact.sendButton')}
      </Button>
      {success && (
        <div className="text-green-600 mt-2 text-center">{success}</div>
      )}
      {error && <div className="text-red-600 mt-2 text-center">{error}</div>}
    </form>
  )
}

function ContactPage() {
  const { t } = useTranslation('static')
  return (
    <div className="container mx-auto max-w-3xl px-4 py-12">
      <div className="mb-8 text-center">
        <div className="flex justify-center mb-2">
          <img
            src="/favicon.svg"
            alt="GearLens"
            width={48}
            height={48}
            className="dark:invert"
          />
        </div>
        <h2 className="text-2xl font-bold mb-1">{t('contact.title')}</h2>
        <p className="text-muted-foreground text-sm">
          {t('contact.subtitle')}
        </p>
      </div>
      <ContactForm />
    </div>
  )
}
